import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Switch,
  Checkbox,
  Collapse,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import ForestOutlinedIcon from "@mui/icons-material/ForestOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import ParkOutlinedIcon from "@mui/icons-material/ParkOutlined";
import { StepProps, isBuildingsAssetSource, isRoadAssetSource } from "../types";
import { useAuth } from "../../../contexts/AuthContext";
import { useDataSources, useDataSourcesByBbox } from "../../../api/dataSources";
import {
  useConstructionTypes,
  defaultWoodConstructionType,
} from "../../../api/constructionTypes";
import { DataSource, SourceType } from "../../../types/dataSource";
import { PROJECT_SIZE_METERS } from "../../../constants/project";
import RoadSourceSelect from "./RoadSourceSelect";

const TEAL = "#00d4ad";

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: TEAL },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: TEAL },
};

const checkboxSx = { color: "#cbd5e1", "&.Mui-checked": { color: TEAL } };

const cardSx = {
  border: "1px solid #e6e8eb",
  boxShadow: "none",
  borderRadius: 2,
};

type Bbox = { top: number; right: number; bottom: number; left: number };

/** Full-width source dropdown backed by the bbox-filtered data-source list. */
const SourceSelect: React.FC<{
  label: string;
  sourceType: SourceType;
  value: string;
  bbox: Bbox;
  onChange: (value: string) => void;
}> = ({ label, sourceType, value, bbox, onChange }) => {
  const { dataSources } = useDataSourcesByBbox(sourceType, bbox);
  return (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select value={value} label={label} onChange={(e) => onChange(String(e.target.value))}>
        {dataSources.map((s) => (
          <MenuItem key={s.id} value={String(s.id)}>
            {s.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}> = ({ icon, title, subtitle, action }) => (
  <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 2.5 }}>
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 1.5,
        backgroundColor: "rgba(0,212,173,0.12)",
        color: TEAL,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#1a1a1a", lineHeight: 1.3 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: "#6b7280" }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {action}
  </Box>
);

/** A left-aligned toggle row used for processing options. */
const ToggleRow: React.FC<{
  title: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ title, description, checked, onChange }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: "#374151" }}>
        {title}
      </Typography>
      <Typography variant="caption" sx={{ color: "#6b7280" }}>
        {description}
      </Typography>
    </Box>
    <Switch checked={checked} onChange={(e) => onChange(e.target.checked)} sx={switchSx} />
  </Box>
);

const DataSourcesStep: React.FC<StepProps> = ({ data, onUpdateData }) => {
  const { user } = useAuth();
  const { dataSources: allAssetSources } = useDataSources("asset");
  const assetSources = allAssetSources.filter((s) => !isRoadAssetSource(s));
  const { dataSources: treatmentSources } = useDataSources("treatment");
  const constructionTypesQ = useConstructionTypes();
  const constructionTypes = constructionTypesQ.data?.data ?? [];

  // Urban environment modelling is gated behind the "Urban" capability
  const hasUrbanCapability = !!(
    user?.is_staff ||
    user?.capabilities?.some((cap) => cap.codename === "urban")
  );

  // The buildings row that the "model urban environment" panel hangs off of.
  const urbanAnchorId = hasUrbanCapability
    ? data.selectedAssetSources.find(isBuildingsAssetSource)?.id
    : undefined;

  // Urban modelling must always carry a default construction type (#203):
  // pre-select the system "Wood" type whenever the toggle is on and nothing is
  // chosen. The wizard blocks Next until a selection exists.
  useEffect(() => {
    if (!data.modelUrbanEnvironment || data.defaultConstructionTypeId !== "") return;
    const wood = defaultWoodConstructionType(constructionTypes);
    if (wood) onUpdateData({ defaultConstructionTypeId: wood.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.modelUrbanEnvironment, data.defaultConstructionTypeId, constructionTypes]);

  const toggleAssetSource = (source: DataSource) => {
    const isSelected = data.selectedAssetSources.some((s) => s.id === source.id);
    onUpdateData({
      selectedAssetSources: isSelected
        ? data.selectedAssetSources.filter((s) => s.id !== source.id)
        : [...data.selectedAssetSources, source],
    });
  };

  const toggleTreatmentSource = (source: DataSource) => {
    const isSelected = data.selectedTreatmentSources.some((s) => s.id === source.id);
    onUpdateData({
      selectedTreatmentSources: isSelected
        ? data.selectedTreatmentSources.filter((s) => s.id !== source.id)
        : [...data.selectedTreatmentSources, source],
    });
  };

  // Bounding box for the vegetation / fire-history source availability filter.
  const calculateBoundingBox = (): Bbox => {
    if (!data.selectedLatLng) return { top: 0, right: 0, bottom: 0, left: 0 };
    const { x: bufferXInit, y: bufferYInit } = PROJECT_SIZE_METERS[data.projectSize];
    let bufferX = bufferXInit;
    let bufferY = bufferYInit;
    if (data.projectAspectRatio === "2:1") bufferX *= 2;
    else if (data.projectAspectRatio === "1:2") bufferY *= 2;

    const earthRadius = 6371000;
    const lat = data.selectedLatLng.lat;
    const lng = data.selectedLatLng.lng;
    const latBuffer = (bufferY / earthRadius) * (180 / Math.PI);
    const lngBuffer = ((bufferX / earthRadius) * (180 / Math.PI)) / Math.cos((lat * Math.PI) / 180);
    return { top: lat + latBuffer, bottom: lat - latBuffer, left: lng - lngBuffer, right: lng + lngBuffer };
  };

  const bbox = calculateBoundingBox();

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", backgroundColor: "#fafbfc" }}>
      {/* Header */}
      <Box sx={{ px: 4, py: 3, backgroundColor: "#fff", borderBottom: "1px solid #e6e8eb" }}>
        <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 600, color: "#1a1a1a" }}>
          Data Sources
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b7280" }}>
          Choose the layers to ingest and tune how infrastructure shapes the vegetation.
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: "auto", px: 4, py: 4 }}>
        <Stack spacing={3} sx={{ maxWidth: 720, mx: "auto" }}>
          {/* Environmental data + road processing */}
          <Card sx={cardSx}>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader
                icon={<ForestOutlinedIcon fontSize="small" />}
                title="Environmental data"
                subtitle="The base vegetation and fire-history layers"
              />
              <Stack spacing={2.5}>
                <SourceSelect
                  label="Vegetation source"
                  sourceType="vegetation"
                  value={data.vegetationSource}
                  bbox={bbox}
                  onChange={(v) => onUpdateData({ vegetationSource: v })}
                />
                <SourceSelect
                  label="Fire history source"
                  sourceType="fire_history"
                  value={data.fireHistorySource}
                  bbox={bbox}
                  onChange={(v) => onUpdateData({ fireHistorySource: v })}
                />
              </Stack>

              <Divider sx={{ my: 2.5 }} />

              <ToggleRow
                title="Stamp roads into vegetation"
                description="Remove road corridors from the vegetation layer so fire doesn't spread across them"
                checked={data.stampRoadsIntoVegetation}
                onChange={(checked) => onUpdateData({ stampRoadsIntoVegetation: checked })}
              />
              <Collapse in={data.stampRoadsIntoVegetation}>
                <Box sx={{ mt: 2, pl: 2, borderLeft: "2px solid #e6e8eb" }}>
                  <RoadSourceSelect
                    value={data.roadSource}
                    bbox={bbox}
                    onChange={(v) => onUpdateData({ roadSource: v })}
                  />
                </Box>
              </Collapse>
            </CardContent>
          </Card>

          {/* Asset sources (with urban modelling attached to the buildings row) */}
          <Card sx={cardSx}>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader
                icon={<LayersOutlinedIcon fontSize="small" />}
                title="Asset sources"
                subtitle="Buildings, power and other assets to ingest"
              />
              {assetSources.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No asset data sources configured
                </Typography>
              ) : (
                <Stack>
                  {assetSources.map((source) => {
                    const isSelected = data.selectedAssetSources.some((s) => s.id === source.id);
                    return (
                      <React.Fragment key={source.id}>
                        <Box
                          onClick={() => toggleAssetSource(source)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            py: 1,
                            borderRadius: 1.5,
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#f8fafc" },
                          }}
                        >
                          <Checkbox checked={isSelected} sx={checkboxSx} disableRipple />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {source.name}
                          </Typography>
                          {source.asset_type && (
                            <Chip
                              label={source.asset_type}
                              size="small"
                              sx={{ height: 18, fontSize: 11, backgroundColor: "#f1f5f9", color: "#64748b" }}
                            />
                          )}
                        </Box>

                        {/* Urban environment modelling lives directly under the buildings row */}
                        {source.id === urbanAnchorId && (
                          <Box sx={{ ml: 5, mb: 1, pl: 2, borderLeft: "2px solid #e6e8eb" }}>
                            <ToggleRow
                              title="Model the urban environment"
                              description="Cut building footprints out of vegetation, classify dense building areas as urban, and apply a default construction type"
                              checked={data.modelUrbanEnvironment}
                              onChange={(checked) => onUpdateData({ modelUrbanEnvironment: checked })}
                            />
                            <Collapse in={data.modelUrbanEnvironment}>
                              <Stack spacing={2.5} sx={{ mt: 2 }}>
                                <FormControl
                                  size="small"
                                  required
                                  error={data.defaultConstructionTypeId === ""}
                                  sx={{ maxWidth: 340 }}
                                >
                                  <InputLabel>Default construction type</InputLabel>
                                  <Select
                                    value={data.defaultConstructionTypeId}
                                    label="Default construction type *"
                                    onChange={(e) =>
                                      onUpdateData({
                                        defaultConstructionTypeId: Number(e.target.value),
                                      })
                                    }
                                  >
                                    {constructionTypes.map((t) => (
                                      <MenuItem key={t.id} value={t.id}>
                                        {t.name}
                                        {t.is_system_default ? " (system)" : ""}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Stack>
                            </Collapse>
                          </Box>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Pre-load treatments — independent of assets */}
          <Card sx={cardSx}>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader
                icon={<ParkOutlinedIcon fontSize="small" />}
                title="Pre-load treatments"
                subtitle="Optionally seed the base strategy with treatments from a data source"
                action={
                  <Chip
                    label="Optional"
                    size="small"
                    sx={{ height: 20, fontSize: 11, backgroundColor: "#f1f5f9", color: "#9ca3af" }}
                  />
                }
              />
              {treatmentSources.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No treatment data sources configured
                </Typography>
              ) : (
                <Stack>
                  {treatmentSources.map((source) => {
                    const isSelected = data.selectedTreatmentSources.some((s) => s.id === source.id);
                    return (
                      <Box
                        key={source.id}
                        onClick={() => toggleTreatmentSource(source)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          py: 1,
                          borderRadius: 1.5,
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "#f8fafc" },
                        }}
                      >
                        <Checkbox checked={isSelected} sx={checkboxSx} disableRipple />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {source.name}
                          </Typography>
                          {typeof source.config?.default_type === "string" && (
                            <Typography variant="caption" color="text.secondary">
                              {source.config.default_type as string}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Box>

      {/* Global navigation bar handles Previous/Next */}
    </Box>
  );
};

export default DataSourcesStep;
